"use client";

import React, { useState, useRef, useEffect } from "react";

type OptionBase = { value: string | number; label: string };

interface MultiSelectProps<T extends OptionBase> {
  options: T[];
  placeholder?: string;
  value?: T[];
  onChange?: (selected: T[]) => void;
}

function MultiSelect<T extends OptionBase>({
  options,
  placeholder = "Select options",
  value,
  onChange,
}: MultiSelectProps<T>) {
  const [internalSelected, setInternalSelected] = useState<T[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const selected = value ?? internalSelected;

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchText(""); // Clear search when closing
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const toggleSelect = (option: T) => {
    let updated: T[];

    if (selected.some((item) => item.value === option.value)) {
      updated = selected.filter((item) => item.value !== option.value);
    } else {
      updated = [...selected, option];
    }

    if (!value) {
      setInternalSelected(updated);
    }
    onChange?.(updated);
    setIsOpen(false);
    setSearchText("");
  };

  // Filter options based on search text
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div className="w-full md:w-64">
      
      {/* Dropdown */}
      <div className="relative" ref={dropdownRef}>
        <div
          onClick={() => setIsOpen(!isOpen)}
          className="rounded-full border border-gray-300 bg-white px-5 py-2 text-gray-700 shadow-sm focus:border-blue-100 focus:outline-none focus:ring-1 focus:ring-blue-100 text-sm md:text-base flex w-full justify-between items-center"
        >
          <span className="text-gray-600">
            {selected.length === 0
              ? placeholder
              : `${selected.length} selected`}
          </span>

          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {isOpen && (
          <div className="absolute w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-hidden z-[35]">
            {/* Search Input */}
            <div className="p-2 border-b sticky top-0 bg-white">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full px-3 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            {/* Options List */}
            <div className="max-h-48 overflow-auto">
              {filteredOptions.length === 0 ? (
                <div className="p-3 text-center text-gray-500 text-sm">
                  No options found
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center gap-2 p-2 hover:bg-gray-100 cursor-pointer text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={selected.some((item) => item.value === option.value)}
                      onChange={() => toggleSelect(option)}
                      className="accent-blue-600"
                    />
                    {option.label}
                  </label>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MultiSelect;
